import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Button, Checkbox, Col, DatePicker, Form, Input, Row, Select, Space, notification, Typography} from "antd";
import dayjs from "dayjs";
import FloatLabel from "../FloatLabel";
import useAdvancedOwner from "../../hooks/useAdvancedOwner";
import AdvanceOwnersTable from "./AdvanceOwnersTable";

const { Title } = Typography;
const initialState = {date_ecriture: null, libelle: null, compte: null, centralized: false, ecritures:[] };
const INITIAL_DATE = dayjs();

export default function FormAcompte({onActive, comptes , addEcriture}) {

  const {data, loading, fetchAdvance, storeAdvance, clearAdvance } = useAdvancedOwner();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [dateSelected, setDateSelected] = useState(INITIAL_DATE);
  const [isCentralized, setIsCentralized] = useState(true);
  const[values, setValues] = useState(initialState);

  const [form] = Form.useForm();
  const [api, contextHolder] = notification.useNotification();

  // Filtre les comptes de type banque
  // useMemo ne recalculera la valeur mémorisée que si l'un des deps a changée.
  const compteBanque = useMemo(() => {
    return comptes?.filter(c => c.compte_type_compte !== "A" && c.compte_compte_general.startsWith("5"))
      .sort((a, b) => Number(a.compte_compte_general) - Number(b.compte_compte_general));
  }, [comptes]);

  const onInit = useCallback(() => {

    // Ne présélectionner que s'il y a un seul compte banque
    const compteId = compteBanque?.length === 1 ? compteBanque[0]?.compte_id : null;

    form.setFieldsValue({
      date: INITIAL_DATE,
      compte: compteId,
      libelle: null,
    });

    setValues(prev => ({
      ...prev,
      compte: compteId,
      date: prev.date ?? INITIAL_DATE,
    }));

    setIsCentralized(true);
    setSelectedRowKeys([]);

  }, [compteBanque, form]);

  useEffect(() => {
    if (onActive) {
      dateSelected !== null
        ? fetchAdvance(dateSelected.format('YYYY-MM-DD')).then()
        : clearAdvance();
    }

    // ⚠️ fetchAdvance et clearAdvance sont exclus volontairement :
    // ils sont mémorisés via useCallback([]) donc leur référence
    // ne change jamais -> inutile de les inclure comme deps.

    //TODO: la dependence onActive est utilisé pour le focus de l'onglet( voir router.js key "61")

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateSelected, onActive]);

  useEffect(() => {

    // On attente compteBancaire pour initialiser le formulaire
    if (compteBanque && compteBanque.length > 0) {
      onInit();
    }
    // ⚠️ onInit exclus volontairement :
    // il est mémorisés via useCallback([]) donc sa référence
    // ne change jamais -> inutile de l'inclure comme deps.

    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[compteBanque]);


  const onDateChange = (date, datestring) => {
    // Normaliser la date en fixant toujours l'heure à 12h pour éviter les doubles déclenchements
    const normalizedDate = date ? date.hour(12).minute(0).second(0).millisecond(0) : null;

    // Vérifier si la date a réellement changé avant de mettre à jour l'état
    if (!normalizedDate || !dateSelected || !normalizedDate.isSame(dateSelected)) {
      setDateSelected(normalizedDate);
    }

    //setDateSelected(date ? date.hour(12).minute(0).second(0).millisecond(0) : null)
    // Vider la table et les sélections si la date est null
    if (!date) {
      setSelectedRowKeys([]);
    }
  }
  
  const handleChange = (changedValues, allValues) => {
    // allValues est déjà un objet { cle: valeur, ... } selon les name
    setValues(allValues);
  };

  const handleFinish = async (values) => {
    // values === formData
    const dateFormat = values.date
      ? values.date.format("YYYY-MM-DD")
      : null;

    const payload = {
      ...values,
      date: dateFormat,
      centralized: isCentralized,
      ecritures: selectedRowKeys
    }

    onInit();
    // on peut aussi faire direct - mais à voir dans le traitement futur
    /*if (values.date) {
      values.date = values.date.format("YYYY-MM-DD");
    }*/

    //console.log("submit:", payload);

    const result = await storeAdvance(payload);
    addEcriture(result.entries)
    if (result.entries) {
      api.success({
        message: 'Acompte propriétaire',
        description: 'Enregistrement effectué avec succès.',
        placement: 'topRight',
      });
    }
    //console.log(result);
    // On recharge les données de la table
    await fetchAdvance(dateSelected.format('YYYY-MM-DD')).then();
  };

  //const handleReset = () => {
    // Vide le formulaire
    //form.resetFields();
    // Réinitialise avec les valeurs par défaut
    //onInit();
  //}

  return (
    <>
    {contextHolder}

    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        /*background: "#f1c4c4",*/
        padding: "0 10px",
        margin: 0,
        boxSizing: "border-box",
      }}
    >
      <Row>
        <Title style={{marginTop: 10}} level={2}>Acompte propriétaire</Title>
      </Row>
      <Space size={"middle"} direction={"vertical"} style={{width:'100%'}}>
      <Row>
        <Col sm={24} lg={8}>
          <FloatLabel label="Date butoir" value={dateSelected} >
            <DatePicker
              defaultValue={dateSelected}
              onChange={onDateChange}
              format="DD/MM/YYYY"
              placeholder=""
            />
          </FloatLabel>
        </Col>

      </Row>
      <Row>
        <Col sm={24} lg={8}>
          <Checkbox
            checked={isCentralized}
            onChange={(e) => setIsCentralized(e.target.checked)}
          >
            Règlements centralisés
          </Checkbox>
        </Col>
      </Row>
      <Row>
        <Col span={24}>
            <AdvanceOwnersTable
              data={data.advance}
              loading={loading}
              selectedRowKeys={selectedRowKeys}
              setSelectedRowKeys={setSelectedRowKeys}
            />
        </Col>
      </Row>

      <Row gutter={10}>
        {/*<Col span={24}>*/}
        <Form
          style={{width:'100%'}}
          form={form}
          layout="inline"
          onValuesChange={handleChange}
          onFinish={handleFinish}
          initialValues={values}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault(); // empêche le submit
            }
          }}
        >
          {/* champs compte */}
          <Col xs={24} lg={8}>
            <FloatLabel label="Compte" value={values.compte} >
              <Form.Item
                style={{marginRight:0}}
                name="compte"
                rules={[{ required: true, message: "Un compte est obligatoire." }]}
              >
                <Select
                  showSearch
                  allowClear
                  optionFilterProp="label"
                  style={{ width: "100%" }}
                  options={[
                    {
                      label: <span>Compte Banque</span>,
                      title: 'Compte Banque',
                      options: compteBanque?.map((item, index) => {
                        return {
                          key:index,
                          value:item.compte_id,
                          label:`${item.compte_compte_general} - ${item.compte_libelle}`
                        }
                      })
                    }]
                  }
                />
              </Form.Item>
            </FloatLabel>
          </Col>

          {/* champs date */}
          <Col xs={24} lg={8}>
            <FloatLabel label="Date écriture" value={values.date}>
              <Form.Item
                style={{marginRight:0}}
                name="date"
                rules={[{ required: true, message: "La date d'écriture est obligatoire." }]}
              >
                <DatePicker
                  format="DD/MM/YYYY"
                  placeholder=""
                />
              </Form.Item>
            </FloatLabel>

          </Col>

          {/* champs Libelle */}
          <Col xs={24} lg={8}>
            <FloatLabel label="Libellé" value={values.libelle}>
              <Form.Item
                style={{marginRight:0}}
                name="libelle"
                rules={[{ required: true, message: "Le libellé est obligatoire." }]}
              >
                <Input
                  placeholder=""
                />
              </Form.Item>
            </FloatLabel>
          </Col>

          <Row gutter={10} style={{ width: "100%" }} justify="end">
            <Col>
              {/*<Space>*/}
                {/*<Button size={"large"} htmlType="button" onClick={handleReset}>Réinitialiser</Button>*/}
                <Button
                  disabled={selectedRowKeys.length === 0}
                  size={"large"}
                  htmlType="submit"
                  type="primary"
                >
                  Valider
                </Button>
              {/*</Space>*/}
            </Col>
          </Row>

        </Form>
        {/*</Col>*/}
      </Row>
      </Space>
    </div>
    </>
  );
}